import { Expense } from "models/Expense";
import { OfficeTranslationKey } from "i18n/officeTranslations";

export interface LedgerAccount {
    value: string;
    number: string;
    labelKey: OfficeTranslationKey;
}

export const LedgerAccountMetadataKey = "ledger_account";
export const UncategorizedLedgerAccount = "uncategorized";

export const ExpenseLedgerAccounts: LedgerAccount[] = [
    { value: "4000", number: "4000", labelKey: "ledgerAccounts.tradeGoods" },
    { value: "4010", number: "4010", labelKey: "ledgerAccounts.materials" },
    { value: "4020", number: "4020", labelKey: "ledgerAccounts.subcontractedWork" },
    { value: "4100", number: "4100", labelKey: "ledgerAccounts.directProjectCosts" },
    { value: "4200", number: "4200", labelKey: "ledgerAccounts.transportAndShipping" },
    { value: "4300", number: "4300", labelKey: "ledgerAccounts.inventoryDifferences" },
    { value: "4400", number: "4400", labelKey: "ledgerAccounts.paymentDifferences" },
    { value: "4500", number: "4500", labelKey: "ledgerAccounts.otherRevenueCost" },
    { value: "4600", number: "4600", labelKey: "ledgerAccounts.purchaseDiscounts" },
    { value: "4700", number: "4700", labelKey: "ledgerAccounts.otherDirectPurchasing" },
    { value: "7000", number: "7000", labelKey: "ledgerAccounts.wagesAndSalaries" },
    { value: "7010", number: "7010", labelKey: "ledgerAccounts.socialSecurity" },
    { value: "7020", number: "7020", labelKey: "ledgerAccounts.pensionCosts" },
    { value: "7030", number: "7030", labelKey: "ledgerAccounts.hiredPersonnel" },
    { value: "7040", number: "7040", labelKey: "ledgerAccounts.trainingCosts" },
    { value: "7100", number: "7100", labelKey: "ledgerAccounts.officeRent" },
    { value: "7110", number: "7110", labelKey: "ledgerAccounts.energyAndWater" },
    { value: "7120", number: "7120", labelKey: "ledgerAccounts.cleaningCosts" },
    { value: "7130", number: "7130", labelKey: "ledgerAccounts.officeMaintenance" },
    { value: "7140", number: "7140", labelKey: "ledgerAccounts.officeSupplies" },
    { value: "7200", number: "7200", labelKey: "ledgerAccounts.softwareAndLicences" },
    { value: "7210", number: "7210", labelKey: "ledgerAccounts.hostingAndCloud" },
    { value: "7220", number: "7220", labelKey: "ledgerAccounts.hardwareAndEquipment" },
    { value: "7230", number: "7230", labelKey: "ledgerAccounts.phoneAndInternet" },
    { value: "7240", number: "7240", labelKey: "ledgerAccounts.itMaintenance" },
    { value: "7300", number: "7300", labelKey: "ledgerAccounts.carCosts" },
    { value: "7310", number: "7310", labelKey: "ledgerAccounts.fuel" },
    { value: "7320", number: "7320", labelKey: "ledgerAccounts.parkingAndTolls" },
    { value: "7330", number: "7330", labelKey: "ledgerAccounts.publicTransport" },
    { value: "7340", number: "7340", labelKey: "ledgerAccounts.travelAndAccommodation" },
    { value: "7350", number: "7350", labelKey: "ledgerAccounts.mileageAllowance" },
    { value: "7400", number: "7400", labelKey: "ledgerAccounts.marketingAndAdvertising" },
    { value: "7410", number: "7410", labelKey: "ledgerAccounts.websiteAndDomains" },
    { value: "7420", number: "7420", labelKey: "ledgerAccounts.representationCosts" },
    { value: "7430", number: "7430", labelKey: "ledgerAccounts.businessGifts" },
    { value: "7440", number: "7440", labelKey: "ledgerAccounts.fairsAndEvents" },
    { value: "7500", number: "7500", labelKey: "ledgerAccounts.accountingCosts" },
    { value: "7510", number: "7510", labelKey: "ledgerAccounts.administrationCosts" },
    { value: "7520", number: "7520", labelKey: "ledgerAccounts.legalAdvice" },
    { value: "7530", number: "7530", labelKey: "ledgerAccounts.otherAdvice" },
    { value: "7600", number: "7600", labelKey: "ledgerAccounts.insurance" },
    { value: "7610", number: "7610", labelKey: "ledgerAccounts.bankCosts" },
    { value: "7620", number: "7620", labelKey: "ledgerAccounts.interestAndFinancing" },
    { value: "7700", number: "7700", labelKey: "ledgerAccounts.subscriptionsAndMemberships" },
    { value: "7710", number: "7710", labelKey: "ledgerAccounts.postageAndPrinting" },
    { value: "7720", number: "7720", labelKey: "ledgerAccounts.smallPurchases" },
    { value: "7730", number: "7730", labelKey: "ledgerAccounts.depreciationCosts" },
    { value: "7800", number: "7800", labelKey: "ledgerAccounts.finesAndAssessments" },
    { value: "7900", number: "7900", labelKey: "ledgerAccounts.otherGeneralCosts" },
];

const LegacyLedgerAccountMap: { [key: string]: string } = {
    purchases: "4000",
    software: "7200",
    office: "7140",
    marketing: "7400",
    travel: "7340",
    phone_internet: "7230",
    bank: "7610",
    administration: "7510",
    insurance: "7600",
    other: "7900",
};

type LedgerTranslator = (key: OfficeTranslationKey) => string;

export const getLedgerAccountOptionLabel = (account: LedgerAccount, t: LedgerTranslator) => `${account.number} - ${t(account.labelKey)}`;

export const getExpenseLedgerAccount = (expense: Expense) => {
    const value = expense.metadata?.[LedgerAccountMetadataKey] || UncategorizedLedgerAccount;
    return LegacyLedgerAccountMap[value] || value;
};

export const getExpenseLedgerAccountLabel = (expense: Expense, t: LedgerTranslator) => {
    const value = getExpenseLedgerAccount(expense);
    const account = ExpenseLedgerAccounts.find(account => account.value === value);
    return account ? getLedgerAccountOptionLabel(account, t) : t("ledgerAccounts.uncategorized");
};

export const setExpenseLedgerAccount = (expense: Expense, value: string) => {
    expense.metadata = {
        ...(expense.metadata || {}),
        [LedgerAccountMetadataKey]: value,
    };

    return expense;
};

export const getLastExpenseLedgerAccount = (expenses: Expense[]) => {
    const expense = expenses
        .filter(expense => getExpenseLedgerAccount(expense) !== UncategorizedLedgerAccount)
        .sort((a, b) => b.GetDate().getTime() - a.GetDate().getTime())[0];

    return expense ? getExpenseLedgerAccount(expense) : UncategorizedLedgerAccount;
};
